import { VisuallyHidden } from "@mantine/core";
import cx from "clsx";
import { type FC, useEffect, useState } from "react";

import styles from "./TrainingEvaluationInterstitial.module.css";

const STEPS = ["Reviewing your call transcript", "Preparing your feedback"] as const;

/**
 * `"submitting"` = the evaluate POST is in flight, which is where most evaluations finish; nothing
 * observable happens in between, so the first step advances on a perception timer. `"scoring"` = the
 * hook is polling, which is a real signal that the judge is running, so the last step is pinned.
 */
export type EvaluationInterstitialPhase = "submitting" | "scoring";

interface Props {
    phase?: EvaluationInterstitialPhase;
    /** The retry overlay is covering this; unmount the steps so the next attempt starts a fresh clock. */
    hasError?: boolean;
    /** The server closed the LiveKit room after Start. Copy only — the disconnect reason is not rendered. */
    closedByServer?: boolean;
}

const INLINE_STEP_ADVANCE_MS = 4_500;
// The measured 94th percentile. Past it the wait genuinely is unusual, and the honest state has to be
// able to sit there until the poll cap and beyond.
const SLOW_THRESHOLD_MS = 30_000;
// A live region is only read when its content *changes*, so the first sentence has to arrive as a
// mutation into a region that is already mounted — rendered-in text is silent. One tick is also the gap
// a screen reader needs between registering the region and being handed something to say.
export const ANNOUNCE_AFTER_MOUNT_MS = 100;

/**
 * The visible ETA sits inside the `aria-hidden` steps card, so each wait state also carries the sentence
 * a screen reader hears. Paired here so the two cannot drift.
 */
const ETA_STATES = {
    normal: {
        visible: "about 20 seconds",
        announced: "Scoring your conversation. This usually takes about 20 seconds.",
    },
    slow: {
        visible: "taking longer than usual",
        announced: "Still scoring your conversation. This is taking longer than usual.",
    },
} as const;

/**
 * Rendered only while there is no error, which is what makes mount-scoped timers correct with no phase
 * bookkeeping: a retry goes error → no-error, so each attempt's wait gets a fresh clock. Both timers must
 * survive the submitting → scoring handover, since the long waits are exactly the ones that poll.
 */
const EvaluationProgressSteps: FC<{ phase: EvaluationInterstitialPhase }> = ({ phase }) => {
    const [timedStep, setTimedStep] = useState(0);
    const [isSlow, setIsSlow] = useState(false);
    // Gates the live region's text, not the region itself: see ANNOUNCE_AFTER_MOUNT_MS.
    const [isAnnouncementReady, setIsAnnouncementReady] = useState(false);

    useEffect(() => {
        const timeouts = [
            setTimeout(() => setIsAnnouncementReady(true), ANNOUNCE_AFTER_MOUNT_MS),
            setTimeout(() => setTimedStep(1), INLINE_STEP_ADVANCE_MS),
            setTimeout(() => setIsSlow(true), SLOW_THRESHOLD_MS),
        ];

        return () => {
            timeouts.forEach(clearTimeout);
        };
    }, []);

    // The last step never auto-completes; it holds until the parent unmounts this interstitial.
    const currentStep = phase === "scoring" ? STEPS.length - 1 : timedStep;
    const eta = ETA_STATES[isSlow ? "slow" : "normal"];

    return (
        <>
            {/* The steps card below is decorative: its labels advance on a perception timer rather than on
                real progress, so announcing them would narrate invented steps. The ETA is the one thing in
                there that carries real information, so it is announced from here instead — one atomic
                sentence, with no step name in it, so nothing is spoken when a step ticks. It mounts empty
                and is filled a tick later, because a live region reads changes, not initial content. */}
            <VisuallyHidden aria-live="polite" aria-atomic="true" data-testid="evaluation-eta-announcement">
                {isAnnouncementReady ? eta.announced : ""}
            </VisuallyHidden>

            <div className={styles.steps} aria-hidden="true">
                <div className={styles.stepsHead}>
                    <span className={styles.stepsHeadTitle}>Scoring your conversation</span>
                    {/* "About 20 seconds" is measured, but it cannot stretch — past SLOW_THRESHOLD_MS it
                        is a claim the expert can see is false. */}
                    <span className={styles.stepsHeadEta} data-testid="evaluation-eta">
                        {eta.visible}
                    </span>
                </div>
                {STEPS.map((label, index) => (
                    <div
                        key={label}
                        data-testid={`evaluation-step-${index}`}
                        data-state={index === currentStep ? "active" : index < currentStep ? "done" : "pending"}
                        className={cx(styles.step, {
                            [styles.active]: index === currentStep,
                            [styles.done]: index < currentStep,
                        })}
                    >
                        <span className={styles.dot} />
                        <span>{label}</span>
                    </div>
                ))}
            </div>

            <p className={styles.autonote}>
                <span className={styles.live} /> Your results will open here automatically — no need to do anything.
            </p>
        </>
    );
};

/**
 * Full-screen "Call complete" interstitial shown between the end of an ASCEND simulation and the
 * arrival of scored results. Ported from `interim-evaluation-screen.mockup.html`.
 *
 * No percentage or progress bar on purpose: nothing reports how far along the judge is, so any number
 * here would be invented.
 */
export const TrainingEvaluationInterstitial: FC<Props> = ({
    phase = "submitting",
    hasError,
    closedByServer = false,
}) => {
    return (
        <div className={styles.overlay} data-testid="evaluation-interstitial" role="status" aria-live="polite">
            <div className={styles.interim}>
                <div className={styles.ringWrap} aria-hidden="true">
                    <div className={styles.ringHalo} />
                    <svg width="132" height="132" viewBox="0 0 132 132">
                        <defs>
                            <linearGradient id="grad" x1="0" y1="0" x2="1" y2="1">
                                <stop offset="0%" stopColor="#8223d2" />
                                <stop offset="100%" stopColor="#a45be8" />
                            </linearGradient>
                        </defs>
                        <circle className={styles.ringTrack} cx="66" cy="66" r="60" />
                        <circle className={styles.ringFill} cx="66" cy="66" r="60" />
                        <circle className={styles.ringArc} cx="66" cy="66" r="60" />
                        <circle className={styles.ringArc2} cx="66" cy="66" r="60" />
                    </svg>
                    <div className={styles.ringGlyph}>
                        <div className={styles.wave}>
                            <span />
                            <span />
                            <span />
                            <span />
                            <span />
                        </div>
                    </div>
                </div>

                <h1 className={styles.headline}>Call complete</h1>
                <p className={styles.reassure}>
                    {closedByServer ? (
                        <>Your call ended because the connection was closed. We&apos;re preparing your results now.</>
                    ) : (
                        <>
                            Your simulation ended when the scenario wrapped up — exactly as it&apos;s meant to.{" "}
                            <b>Nice work getting all the way through it.</b>
                        </>
                    )}
                </p>

                {!hasError && <EvaluationProgressSteps phase={phase} />}
            </div>
        </div>
    );
};

export default TrainingEvaluationInterstitial;
